import PropTypes from "prop-types"
import React, { useState, useMemo, useEffect } from "react"
import {
  Col,
  Row,
  Modal,
  Button,
  ModalHeader,
  ModalBody,
  Label,
  FormFeedback,
  Input,
  Form,
  InputGroup,
} from "reactstrap"
import * as Yup from "yup"
import { useFormik } from "formik"
import Select from "react-select"
import {
  addNewCarPark as onAddNewCarPark,
  updateCarPark as onUpdateCarPark,
  clearMessage as onClearMessage,
} from "store/carpark/actions"
import { convertUtcToLocal, convertDateToUtc } from "../../helpers/util"
import { map, isEmpty } from "lodash"
import toastr from "toastr"
import "toastr/build/toastr.min.css"
import Switch from "react-switch"
import "react-datepicker/dist/react-datepicker.css"
import OffSymbol from "components/Common/OffSymbol"
import OnSymbol from "components/Common/OnSymobl"
import {
  seasonPasstSatusTypes,
  priceTypes,
  messageTitle,
  vehicleTypes,
} from "../../constants/common"
import { getSettingData } from "../../store/settings/actions"
//redux
import { useSelector, useDispatch } from "react-redux"
import moment from "moment"
import { dateformat } from "config"
const defaultDate = moment()
const defaultExpiryDate = moment().add(1, "days")
const getPriceType = carpark => {
  if (!isEmpty(carpark)) {
    return {
      value: carpark?.pricingType || "",
      label: carpark?.pricingType || "",
    }
  }
  return null
}

const getPMS = carpark => {
  if (!isEmpty(carpark)) {
    return {
      value: carpark?.pmsId || "",
      label: carpark?.pmsId || "",
    }
  }
  return null
}

const getScheduleDate = carpark => {
  if (!isEmpty(carpark)) {
    const schedule = carpark?.scheduled || null
    if (schedule && !isEmpty(schedule)) {
      return schedule[0].dateScheduled
    }
  }

  return defaultDate
}

const getScheduleChangeDate = carpark => {
  if (!isEmpty(carpark)) {
    const schedule = (carpark?.scheduled || []).find(
      el => el.updateField == "carParkTitle" || el.updateField == "status"
    )
    if (schedule && !isEmpty(schedule)) {
      return schedule.dateScheduled
    } else {
      return carpark?.liveDate || defaultDate
    }
  }
  return defaultDate
}

const getExpiryDate = carpark => {
  if (!isEmpty(carpark)) {
    const hasExpiry = (carpark?.scheduled || []).find(
      el => el.updateField == "expiryDate"
    )
    if (hasExpiry && !isEmpty(hasExpiry)) {
      return hasExpiry.updateData
    } else {
      return carpark?.expiryDate || defaultDate
    }
  }
  return defaultDate
}

const getLots = (selectedCarpark, type) => {
  if (Array.isArray(selectedCarpark?.vehicleType)) {
    const obj = selectedCarpark?.vehicleType.find(el => el.vehicleType == type)
    if (obj) {
      return obj.lots
    }
  } else {
    return selectedCarpark?.lots || 0
  }
}

const CarParkCreate = ({
  modal = false,
  toggle,
  selectedCarpark,
  isEdit,
  setSelectedCarpark,
  setIsEdit,
}) => {
  const dispatch = useDispatch()
  const [liveStatus, setLiveStatus] = useState(true)
  const [scheduleChange, setScheduleChange] = useState(false)
  const [expiryStatus, setExpiryStatus] = useState(false)
  const [selectedType, setType] = useState(getPriceType(selectedCarpark))
  const [selectedPMS, setPMS] = useState(getPMS(selectedCarpark))
  const [price, setPricing] = useState(0)
  const [bikePrice, setBikePricing] = useState(0)
  const [selectedBikeType, setBikeType] = useState(
    getPriceType(selectedCarpark)
  )
  // validation
  const validation = useFormik({
    // enableReinitialize : use this flag when initial values needs to be changed
    enableReinitialize: true,

    initialValues: {
      carParkTitle: (selectedCarpark && selectedCarpark.carParkTitle) || "",
      carParkShortCode:
        (selectedCarpark && selectedCarpark.carParkShortCode) || "",
      lots: (selectedCarpark && getLots(selectedCarpark, "car")) || "0",
      bikeLots: (selectedCarpark && getLots(selectedCarpark, "bike")) || "0",
      scheduleDate: convertUtcToLocal(getScheduleDate(selectedCarpark || null)),
      expiryDate: convertUtcToLocal(getExpiryDate(selectedCarpark || null)),
      scheduleChangeDate: convertUtcToLocal(
        getScheduleChangeDate(selectedCarpark || null)
      ),
    },
    validationSchema: Yup.object({
      carParkTitle: Yup.string().required("Please Enter Your Title"),
      carParkShortCode: Yup.string()
        .required("Please Enter Your Code")
        .matches(/^[A-Z|a-z]+$/, "Only Characters")
        .max(3, "Please Enter Maximum 3 Characters Only")
        .min(2, "Please Enter Minimum 2 Characters Only"),
      lots: Yup.string()
        .required("Please Enter Your Lots")
        .matches(/^[#0-9]+$/, "Only Number")
        .max(3, "Please Enter Maximum 3 Numbers Only"),
      bikeLots: Yup.string()
        .matches(/^[#0-9]+$/, "Only Number")
        .max(3, "Please Enter Maximum 3 Numbers Only"),
    }),
    onSubmit: values => {
      if (isEmpty(selectedPMS?.value)) {
        setCustValidation({ validPMS: false })
        return
      }

      const _status = !liveStatus
        ? seasonPasstSatusTypes["Not-Available"]
        : liveStatus &&
          convertUtcToLocal(defaultDate, "YYYY-MM-DD") ==
            convertUtcToLocal(values["scheduleDate"], "YYYY-MM-DD")
        ? seasonPasstSatusTypes.Available
        : seasonPasstSatusTypes.Pending

      let carParkObj = {
        carParkTitle: values["carParkTitle"],
        carParkShortCode: values["carParkShortCode"],
        lots: values["lots"],
        pricing: price,
        pricingType: selectedType?.value || "minute",
        expiryDate: expiryStatus
          ? convertDateToUtc(values["expiryDate"])
          : null,
        status: _status,
        liveDate:
          _status == seasonPasstSatusTypes.Pending
            ? convertDateToUtc(values["scheduleDate"])
            : convertDateToUtc(defaultDate),
        pmsId: selectedPMS?.value || "",
        vehicleType: [
          {
            vehicleType: vehicleTypes.car,
            lots: values["lots"],
            pricingType: selectedType?.value || "minute",
            pricing: price,
            availableLots: values["lots"],
            usedLots: 0,
          },
          {
            vehicleType: vehicleTypes.bike,
            lots: values["bikeLots"],
            pricingType: selectedBikeType?.value || "minute",
            pricing: bikePrice,
            availableLots: values["bikeLots"],
            usedLots: 0,
          },
        ],
      }
      if ((_status == "Pending" || scheduleChange) && isEdit) {
        let _scheduleObj = []
        if (selectedCarpark?.carParkTitle != carParkObj?.carParkTitle) {
          _scheduleObj.push({
            dateScheduled: scheduleChange
              ? convertDateToUtc(values["scheduleChangeDate"])
              : convertDateToUtc(values["scheduleDate"]),
            updateField: "carParkTitle",
            updateData: carParkObj?.carParkTitle,
          })
          delete carParkObj?.carParkTitle
        }
        if (selectedCarpark?.carParkShortCode != carParkObj?.carParkShortCode) {
          _scheduleObj.push({
            dateScheduled: scheduleChange
              ? convertDateToUtc(values["scheduleChangeDate"])
              : convertDateToUtc(values["scheduleDate"]),
            updateField: "carParkShortCode",
            updateData: carParkObj?.carParkShortCode,
          })
          delete carParkObj?.carParkShortCode
        }
        if (selectedCarpark?.lots != carParkObj?.lots) {
          _scheduleObj.push({
            dateScheduled: scheduleChange
              ? convertDateToUtc(values["scheduleChangeDate"])
              : convertDateToUtc(values["scheduleDate"]),
            updateField: "lots",
            updateData: carParkObj?.lots,
          })
          delete carParkObj?.lots
        }
        if (selectedCarpark?.pricing != carParkObj?.pricing) {
          _scheduleObj.push({
            dateScheduled: scheduleChange
              ? convertDateToUtc(values["scheduleChangeDate"])
              : convertDateToUtc(values["scheduleDate"]),
            updateField: "pricing",
            updateData: carParkObj?.pricing,
          })
          _scheduleObj.push({
            dateScheduled: scheduleChange
              ? convertDateToUtc(values["scheduleChangeDate"])
              : convertDateToUtc(values["scheduleDate"]),
            updateField: "pricingType",
            updateData: carParkObj?.pricingType,
          })
          delete carParkObj?.pricing
          delete carParkObj?.pricingType
        }

        if (
          convertDateToUtc(selectedCarpark?.expiryDate) !=
            carParkObj?.expiryDate &&
          expiryStatus
        ) {
          _scheduleObj.push({
            dateScheduled: scheduleChange
              ? convertDateToUtc(values["scheduleChangeDate"])
              : convertDateToUtc(values["scheduleDate"]),
            updateField: "expiryDate",
            updateData: carParkObj?.expiryDate,
          })
          delete carParkObj?.expiryDate
        }
        carParkObj = {
          ...carParkObj,
          scheduled: _scheduleObj,
        }
      } else if (_status == "Pending" && !isEdit) {
        carParkObj = {
          ...carParkObj,
          scheduled: [
            {
              dateScheduled: convertDateToUtc(values["scheduleDate"]),
              updateField: "status",
              updateData: seasonPasstSatusTypes.Available,
            },
          ],
        }
      }
      //console.log(`carParkObj`, carParkObj)

      if (isEdit) {
        carParkObj = {
          ...carParkObj,
          id: selectedCarpark._id,
          vehicleType: [
            {
              ...carParkObj.vehicleType[0],
              usedLots: selectedCarpark.vehicleType[0].usedLots,
              availableLots: selectedCarpark.vehicleType[0].availableLots,
            },
            {
              ...carParkObj.vehicleType[1],
              usedLots: selectedCarpark.vehicleType[1].usedLots,
              availableLots: selectedCarpark.vehicleType[1].availableLots,
            },
          ],
        }
        //console.log(`Updating..................`, carParkObj)
        dispatch(onUpdateCarPark(carParkObj))
      } else {
        //console.log(`onAddNewCarPark`, carParkObj)
        dispatch(onAddNewCarPark(carParkObj))
      }
    },
  })

  const [customValidation, setCustValidation] = useState({
    validPMS: true,
  })
  const [primaryStations, setPrimaryStations] = useState([])

  const { message, error, settingData } = useSelector(state => ({
    message: state.carPark.message,
    error: state.carPark.error,
    settingData: state.Settings.settingData,
  }))

  useEffect(() => {
    dispatch(getSettingData())
  }, [])

  useEffect(() => {
    if (settingData["cpfProperties"]) {
      const { cpfBuilding } = settingData["cpfProperties"]
      const list = !isEmpty(cpfBuilding) ? cpfBuilding.split(`,`) : []
      setPrimaryStations(list)
    }
  }, [settingData])

  useMemo(() => {
    if (!isEmpty(selectedCarpark)) {
      setLiveStatus(
        selectedCarpark?.status != seasonPasstSatusTypes["Not-Available"] &&
          selectedCarpark?.status != seasonPasstSatusTypes.Removed
      )
    }
    setExpiryStatus(selectedCarpark?.expiryDate)
    setPMS(getPMS(selectedCarpark) || null)
    setScheduleChange(
      !isEmpty(selectedCarpark?.scheduled) &&
        selectedCarpark?.status == seasonPasstSatusTypes.Approved
    )
    if (Array.isArray(selectedCarpark?.vehicleType)) {
      selectedCarpark?.vehicleType.forEach(el => {
        if (el.vehicleType == "car") {
          setType(getPriceType(el || null))
          setPricing(el?.pricing || 0)
        } else {
          setBikeType(getPriceType(el || null))
          setBikePricing(el?.pricing || 0)
        }
      })
    } else {
      setType(getPriceType(selectedCarpark || null))
      setPricing(selectedCarpark?.pricing || 0)
    }
  }, [selectedCarpark])

  useMemo(() => {
    if (message) {
      setTimeout(() => {
        dispatch(onClearMessage())
      }, 500)
      toastr.success(message, messageTitle)
      validation.resetForm()
      setIsEdit(false)
      setLiveStatus(true)
      setCustValidation({
        validPMS: true,
      })
      setPricing(0)
      setBikePricing(0)
      setPMS(null)
      setSelectedCarpark(null)
      toggle()
    }
  }, [dispatch, message])

  useMemo(() => {
    if (!isEmpty(error)) {
      toastr.error(error.message, messageTitle)
      setTimeout(() => {
        dispatch(onClearMessage())
      }, 500)
    }
  }, [dispatch, error])

  const priceTypeOptions = map(priceTypes, (key, i) => {
    return {
      value: `${key}`,
      label: `${priceTypes[key]}`,
    }
  })

  const stationOptions = map(primaryStations, key => {
    return {
      value: `${key.trim()}`,
      label: `${key.trim()}`,
    }
  })

  const _scheduleDate = validation.values.scheduleDate
    ? moment(validation.values.scheduleDate).format(dateformat.default)
    : ""
  const _expiryDate = validation.values.expiryDate
    ? moment(validation.values.expiryDate).format(dateformat.default)
    : ""

  const _scheduleChangeDate = validation.values.scheduleChangeDate
    ? moment(validation.values.scheduleChangeDate).format(dateformat.default)
    : ""
  //console.log("liveStatus", liveStatus)
  return (
    <Modal className={"modal-lg"} isOpen={modal} toggle={toggle}>
      <ModalHeader toggle={toggle} tag="h4">
        {!!isEdit ? "Update Carpark" : "Add New Carpark"}
      </ModalHeader>
      <ModalBody>
        <Form
          onSubmit={e => {
            e.preventDefault()
            validation.handleSubmit()
            return false
          }}
        >
          <Row form>
            <Col xs={12}>
              {/* <Row>
                <Col md={6}>
                  <div className="mb-2">
                    <Label className="form-label">Live</Label>
                    <Row className="mb-2">
                      <Col md={2} className="pr-0 pt-1">
                        <Switch
                          checked={liveStatus}
                          onColor="#626ed4"
                          onChange={() => setLiveStatus(!liveStatus)}
                          uncheckedIcon={<OffSymbol text="Off" />}
                          checkedIcon={<OnSymbol text="On" />}
                        />
                      </Col>
                      {liveStatus && (
                        <Col md={8} className="pl-0">
                          <input
                            className="form-control"
                            type="date"
                            defaultValue={_scheduleDate}
                            id="scheduleDate"
                            name="scheduleDate"
                            min={moment().format("YYYY-MM-DD")}
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            invalid={
                              validation.touched.scheduleDate &&
                              validation.errors.scheduleDate
                                ? true
                                : false
                            }
                            // disabled={
                            //   liveStatus &&
                            //   isEdit &&
                            //   selectedCarpark?.status == seasonPasstSatusTypes.Available
                            // }
                          />
                          {validation.touched.scheduleDate &&
                          validation.errors.scheduleDate ? (
                            <FormFeedback type="invalid">
                              {validation.errors.scheduleDate}
                            </FormFeedback>
                          ) : null}
                        </Col>
                      )}
                    </Row>
                  </div>
                </Col>
                <Col md={6}>
                  <Label className="form-label">Expiry Date</Label>
                  <Row className="mb-4">
                    <Col md={2} className="pr-0 pt-1">
                      <Switch
                        checked={expiryStatus}
                        onColor="#626ed4"
                        onChange={() => setExpiryStatus(!expiryStatus)}
                        uncheckedIcon={<OffSymbol text="Off" />}
                        checkedIcon={<OnSymbol text="On" />}
                        //disabled={true}
                      />
                    </Col>
                    {expiryStatus && (
                      <Col md={8} className="pl-0">
                        <input
                          className="form-control"
                          type="date"
                          defaultValue={_expiryDate}
                          min={moment().format("YYYY-MM-DD")}
                          id="expiryDate"
                          name="expiryDate"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          invalid={
                            validation.touched.expiryDate &&
                            validation.errors.expiryDate
                              ? true
                              : false
                          }
                        />
                        {validation.touched.expiryDate &&
                        validation.errors.expiryDate ? (
                          <FormFeedback type="invalid">
                            {validation.errors.expiryDate}
                          </FormFeedback>
                        ) : null}
                      </Col>
                    )}
                  </Row>
                </Col>
              </Row> */}
              {/* {!isEmpty(selectedCarpark) &&
                (selectedCarpark?.status == seasonPasstSatusTypes.Approved ||
                  selectedCarpark?.status == seasonPasstSatusTypes.Available) && (
                  <Row className="mb-2">
                    <Col md={12}>
                      <Label className="col-form-label">Schedule Change</Label>
                      <Row className="mb-2">
                        <Col md={2} className="pr-0 pt-1">
                          <Switch
                            checked={scheduleChange}
                            onColor="#626ed4"
                            onChange={() => setScheduleChange(!scheduleChange)}
                            uncheckedIcon={<OffSymbol text="Off" />}
                            checkedIcon={<OnSymbol text="On" />}
                            //disabled={true}
                          />
                        </Col>
                        {scheduleChange && (
                          <Col md={10} className="pl-0">
                            <input
                              className="form-control"
                              type="date"
                              defaultValue={_scheduleChangeDate}
                              id="scheduleChangeDate"
                              name="scheduleChangeDate"
                              min={moment().format("YYYY-MM-DD")}
                              onChange={validation.handleChange}
                              onBlur={validation.handleBlur}
                              invalid={
                                validation.touched.scheduleChangeDate &&
                                validation.errors.scheduleChangeDate
                                  ? true
                                  : false
                              }
                            />
                            {validation.touched.scheduleChangeDate &&
                            validation.errors.scheduleChangeDate ? (
                              <FormFeedback type="invalid">
                                {validation.errors.scheduleChangeDate}
                              </FormFeedback>
                            ) : null}
                          </Col>
                        )}
                      </Row>
                    </Col>
                  </Row>
                )} */}

              <Row>
                <Col xs={8}>
                  <div className="mb-2">
                    <Label className="form-label">Primary Location</Label>
                    <Select
                      name={"pmsId"}
                      value={selectedPMS}
                      onChange={e => {
                        setCustValidation({
                          validPMS: true,
                        })
                        setPMS(e)
                      }}
                      options={stationOptions}
                      classNamePrefix="select2-selection"
                    />

                    {!customValidation.validPMS && (
                      <div
                        className="invalid-feedback"
                        style={{ display: "block" }}
                      >
                        Please select primary station
                      </div>
                    )}
                  </div>
                </Col>
              </Row>
              <Row>
                <Col xs={9}>
                  <div className="mb-2">
                    <Label className="form-label">Carpark Title*</Label>
                    <Input
                      name="carParkTitle"
                      type="text"
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.carParkTitle || ""}
                      invalid={
                        validation.touched.carParkTitle &&
                        validation.errors.carParkTitle
                          ? true
                          : false
                      }
                    />
                    {validation.touched.carParkTitle &&
                    validation.errors.carParkTitle ? (
                      <FormFeedback type="invalid">
                        {validation.errors.carParkTitle}
                      </FormFeedback>
                    ) : null}
                  </div>
                </Col>
                <Col xs={3}>
                  <div className="mb-2">
                    <Label className="form-label">Carpark Code*</Label>
                    <Input
                      name="carParkShortCode"
                      type="text"
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.carParkShortCode || ""}
                      invalid={
                        validation.touched.carParkShortCode &&
                        validation.errors.carParkShortCode
                          ? true
                          : false
                      }
                    />
                    {validation.touched.carParkShortCode &&
                    validation.errors.carParkShortCode ? (
                      <FormFeedback type="invalid">
                        {validation.errors.carParkShortCode}
                      </FormFeedback>
                    ) : null}
                  </div>
                </Col>
              </Row>
              <div className="mb-3">
                <h5 className="font-size-14 mb-3 mt-3">Car</h5>
              </div>
              <Row>
                <Col xs={4}>
                  <div className="mb-2 mt-2">
                    <Label className="form-label">Lots Available*</Label>
                    <Input
                      name="lots"
                      type="text"
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.lots || ""}
                      invalid={
                        validation.touched.lots && validation.errors.lots
                          ? true
                          : false
                      }
                    />
                    {validation.touched.lots && validation.errors.lots ? (
                      <FormFeedback type="invalid">
                        {validation.errors.lots}
                      </FormFeedback>
                    ) : null}
                  </div>
                </Col>
                <Col lg={8}>
                  <Label className="col-form-label col-lg-2">Price</Label>
                  <Row className="mb-2">
                    <Col md={6} className="pr-0">
                      <Input
                        name="price"
                        type="number"
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                        pattern="^\d+(?:\.\d{1,2})?$"
                        onChange={e => setPricing(e.target.value)}
                        value={price}
                        invalid={
                          validation.touched.price && validation.errors.price
                            ? true
                            : false
                        }
                      />
                    </Col>
                    <Col md={1} className="pl-0 pr-0 pt-2">
                      /
                    </Col>
                    <Col md={5} className="pl-0 pr-0">
                      <Select
                        name={"seasonType"}
                        value={selectedType}
                        onChange={setType}
                        options={priceTypeOptions}
                        classNamePrefix="select2-selection"
                      />
                    </Col>
                  </Row>
                </Col>
              </Row>
              <div className="mb-3">
                <h5 className="font-size-14 mb-3 mt-3">Bike</h5>
              </div>
              <Row>
                <Col xs={4}>
                  <div className="mb-2 mt-2">
                    <Label className="form-label">Lots Available*</Label>
                    <Input
                      name="bikeLots"
                      type="text"
                      onChange={validation.handleChange}
                      onBlur={validation.handleBlur}
                      value={validation.values.bikeLots || ""}
                      invalid={
                        validation.touched.bikeLots &&
                        validation.errors.bikeLots
                          ? true
                          : false
                      }
                    />
                    {validation.touched.bikeLots &&
                    validation.errors.bikeLots ? (
                      <FormFeedback type="invalid">
                        {validation.errors.bikeLots}
                      </FormFeedback>
                    ) : null}
                  </div>
                </Col>
                <Col lg={8}>
                  <Label className="col-form-label col-lg-2">Price</Label>
                  <Row className="mb-2">
                    <Col md={6} className="pr-0">
                      <Input
                        name="bikePrice"
                        type="number"
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                        pattern="^\d+(?:\.\d{1,2})?$"
                        onChange={e => setBikePricing(e.target.value)}
                        value={bikePrice}
                        invalid={
                          validation.touched.bikePrice &&
                          validation.errors.bikePrice
                            ? true
                            : false
                        }
                      />
                    </Col>
                    <Col md={1} className="pl-0 pr-0 pt-2">
                      /
                    </Col>
                    <Col md={5} className="pl-0 pr-0">
                      <Select
                        name={"bikePriceType"}
                        value={selectedBikeType}
                        onChange={setBikeType}
                        options={priceTypeOptions}
                        classNamePrefix="select2-selection"
                      />
                    </Col>
                  </Row>
                </Col>
              </Row>
            </Col>
          </Row>
          <Row>
            <Col>
              <div className="text-end mt-4">
                <button type="submit" className="btn btn-success save-user">
                  {!!isEdit ? "Update Carpark" : "Add New Carpark"}
                </button>
              </div>
            </Col>
          </Row>
        </Form>
      </ModalBody>
    </Modal>
  )
}

export default CarParkCreate
CarParkCreate.propTypes = {
  modal: PropTypes.bool,
  selectedCarpark: PropTypes.object,
  toggle: PropTypes.func,
  setSelectedCarpark: PropTypes.func,
  isEdit: PropTypes.bool,
  setIsEdit: PropTypes.func,
}
